
import React, { useRef, useMemo, useEffect, useState } from 'react';
import { useFrame, useLoader, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { gsap } from 'gsap';
import { PROJECT_COUNT, ITEM_SPACING } from '../constants';
import { cardShader } from '../shaders/cardShader';

// Fix R3F intrinsic element type errors by using type-safe component wrappers
const ThreeMesh = 'mesh' as any;
const ThreePlaneGeometry = 'planeGeometry' as any;
const ThreePrimitive = 'primitive' as any;

interface CardProps {
  index: number;
  project: any;
  scrollRef: React.MutableRefObject<number>;
  velocityRef: React.MutableRefObject<number>;
  isVisible: boolean;
}

export const Card: React.FC<CardProps> = ({ index, project, scrollRef, velocityRef, isVisible }) => {
  const meshRef = useRef<THREE.Mesh>(null);
  const { viewport } = useThree();
  const [isHovered, setIsHovered] = useState(false);
  const sectionOpacity = useRef({ value: 0 }); 
  const hoverScaleRef = useRef(0); 

  // Detect if we're on a narrow viewport (mobile)
  const isMobile = viewport.width < 8;

  // Load the project image with explicit crossOrigin and error handling
  const texture = useLoader(THREE.TextureLoader, project.image, (loader) => {
    loader.setCrossOrigin('anonymous');
  });

  useEffect(() => {
    if (texture && !Array.isArray(texture)) {
      const tex = texture as THREE.Texture;
      tex.anisotropy = 16;
      tex.minFilter = THREE.LinearFilter;
      tex.magFilter = THREE.LinearFilter;
      tex.needsUpdate = true;
    }
  }, [texture]);

  // Generate a UI overlay texture (Title, Category, etc.)
  const uiTexture = useMemo(() => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    // Use higher resolution for crisp text
    canvas.width = 1024;
    canvas.height = 1400;

    // Clear transparent background
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Subtle Vignette for text readability
    const grad = ctx.createLinearGradient(0, canvas.height, 0, canvas.height * 0.5);
    grad.addColorStop(0, 'rgba(0,0,0,0.85)');
    grad.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, canvas.height * 0.5, canvas.width, canvas.height * 0.5);

    // Responsive font sizing for the canvas texture
    const titleSize = isMobile ? '120px' : '140px';
    const subtextSize = isMobile ? '24px' : '20px';
    const headerSize = isMobile ? '28px' : '24px';

    // Decorative Header
    ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.font = `bold ${headerSize} "JetBrains Mono"`;
    ctx.fillText(`0${project.id + 1}`, 60, 100);
    ctx.fillText('ARCHIVE_MOD', canvas.width - (isMobile ? 300 : 240), 100);

    // Bottom Content
    ctx.fillStyle = '#ffffff';
    ctx.font = `900 italic ${titleSize} "Inter"`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'bottom';
    ctx.fillText(project.title.toUpperCase(), 60, canvas.height - 140);

    ctx.fillStyle = '#8888ff';
    ctx.font = `bold ${subtextSize} "JetBrains Mono"`;
    ctx.letterSpacing = isMobile ? '4px' : '8px';
    ctx.fillText(project.category.toUpperCase(), 60, canvas.height - 80);

    const t = new THREE.CanvasTexture(canvas);
    t.needsUpdate = true;
    t.anisotropy = 16;
    return t;
  }, [project, isMobile]);
  
  const material = useMemo(() => {
    return new THREE.ShaderMaterial({
      uniforms: {
        uTexture: { value: texture },
        uUITexture: { value: uiTexture },
        uTime: { value: 0 },
        uVelocity: { value: 0 },
        uOpacity: { value: 0 },
        uBend: { value: 0.05 },
        uHover: { value: 0 },
      },
      vertexShader: cardShader.vertex,
      fragmentShader: cardShader.fragment,
      transparent: true,
      side: THREE.DoubleSide
    });
  }, [texture, uiTexture]);

  useEffect(() => {
    gsap.to(sectionOpacity.current, {
      value: isVisible ? 1 : 0,
      duration: 1.2,
      ease: "power2.out"
    });
  }, [isVisible]);

  const handlePointerOver = () => {
    if (!isVisible) return;
    setIsHovered(true);
    gsap.to(material.uniforms.uHover, { value: 1, duration: 0.4, ease: "power2.out" });
    document.body.style.cursor = 'pointer';
  };

  const handlePointerOut = () => {
    setIsHovered(false);
    gsap.to(material.uniforms.uHover, { value: 0, duration: 0.4, ease: "power2.out" });
    document.body.style.cursor = 'auto';
  };

  useFrame((state) => {
    if (!meshRef.current) return;

    const totalWidth = PROJECT_COUNT * ITEM_SPACING;
    const initialPos = index * ITEM_SPACING;
    
    let x = (initialPos - scrollRef.current) % totalWidth;
    if (x > totalWidth / 2) x -= totalWidth;
    if (x < -totalWidth / 2) x += totalWidth;

    const radius = 7.5; 
    const angle = x / radius;

    const newX = radius * Math.sin(angle);
    const newZ = radius * (Math.cos(angle) - 1); 

    meshRef.current.position.set(newX, 0, newZ);
    meshRef.current.rotation.y = -angle;

    const distFromCenter = Math.abs(x);
    
    // ADJUSTED SCALING: We multiply by a smaller factor on mobile (0.65 instead of 1.15)
    const baseScale = isMobile ? 0.65 : 1.15;
    const minScale = isMobile ? 0.4 : 0.7;
    
    const distScale = THREE.MathUtils.lerp(
      baseScale, 
      minScale, 
      THREE.MathUtils.smoothstep(0, totalWidth / 2.2, distFromCenter)
    );
    
    const targetHoverScale = isHovered ? (isMobile ? 0.05 : 0.1) : 0;
    hoverScaleRef.current = THREE.MathUtils.lerp(hoverScaleRef.current, targetHoverScale, 0.1);
    
    const finalScale = distScale + hoverScaleRef.current;
    meshRef.current.scale.set(finalScale, finalScale, finalScale);

    const distFade = 1.0 - THREE.MathUtils.smoothstep(5.5, 12.0, distFromCenter);
    
    material.uniforms.uOpacity.value = sectionOpacity.current.value * distFade;
    material.uniforms.uTime.value = state.clock.elapsedTime;
    
    const velocity = Math.abs(velocityRef.current);
    material.uniforms.uVelocity.value = THREE.MathUtils.lerp(
      material.uniforms.uVelocity.value,
      velocity * 0.12,
      0.1
    );
  });

  return (
    <ThreeMesh 
      ref={meshRef} 
      onPointerOver={handlePointerOver}
      onPointerOut={handlePointerOut}
    >
      <ThreePlaneGeometry args={[3.2, 4.4, 64, 64]} />
      <ThreePrimitive object={material} attach="material" />
    </ThreeMesh>
  );
};
